// components/account/AccountHistoricsModal/hooks/usePagination.js
import { useState, useEffect, useMemo } from 'react';

/**
 * Hook personnalisé pour gérer la pagination front-end
 * @param {Array} data - Données à paginer
 * @param {Object} options - Options de pagination
 * @param {number} options.pageSize - Taille de page par défaut (15)
 * @param {boolean} options.autoReset - Réinitialiser à la page 1 quand les données changent
 */
export const usePagination = (data = [], options = {}) => {
  const {
    pageSize: defaultPageSize = 15,
    autoReset = true
  } = options;

  // États de pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Réinitialiser à la page 1 quand les données changent
  useEffect(() => {
    if (autoReset && data.length > 0) {
      setCurrentPage(1);
    }
  }, [data.length, autoReset]);

  /**
   * Calcule les données paginées
   */
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  /**
   * Calcule le nombre total de pages
   */
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(data.length / pageSize));
  }, [data.length, pageSize]);

  /**
   * Vérifie si on peut aller à la page suivante
   */
  const hasNextPage = currentPage < totalPages;

  /**
   * Vérifie si on peut aller à la page précédente
   */
  const hasPreviousPage = currentPage > 1;

  /**
   * Va à la page spécifiée
   */
  const goToPage = (page) => {
    const pageNumber = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(pageNumber);
  };

  /**
   * Va à la page suivante
   */
  const nextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  /**
   * Va à la page précédente
   */
  const previousPage = () => {
    if (hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  /**
   * Va à la première page
   */
  const firstPage = () => {
    setCurrentPage(1);
  };

  /**
   * Va à la dernière page
   */
  const lastPage = () => {
    setCurrentPage(totalPages);
  };

  /**
   * Change la taille de page
   */
  const changePageSize = (newSize) => {
    const validSize = Math.max(1, parseInt(newSize, 10) || defaultPageSize);
    setPageSize(validSize);
    setCurrentPage(1); // Retour à la première page quand on change la taille
  };

  return {
    // États
    currentPage,
    pageSize,
    totalPages,
    
    // Données
    paginatedData,
    totalItems: data.length,
    
    // Navigation
    hasNextPage,
    hasPreviousPage,
    goToPage,
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    
    // Configuration
    changePageSize,
    
    // Utilitaires
    startIndex: (currentPage - 1) * pageSize + 1,
    endIndex: Math.min(currentPage * pageSize, data.length)
  };
};